import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AtSign, Lock, Loader2, ArrowLeft, Globe, Instagram, KeyRound, ShieldCheck } from 'lucide-react';
import { translations } from '../../translations';
import { hikerApi } from '../../services/hikerApi';
import { supabase } from '../../lib/supabase';
import { validateInstagramUsername, isValidInstagramChar } from '../../utils/validation';
import { countries } from '../../data/countries';

interface AddAccountProps {
  isDarkMode: boolean;
  language: string;
  onAddAccount: (accountData: any) => Promise<void>;
}

export function AddAccount({ isDarkMode, language, onAddAccount }: AddAccountProps) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [country, setCountry] = useState('France');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'pending' | 'connected' | 'failed' | '2fa'>('pending');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [is2FASubmitting, setIs2FASubmitting] = useState(false);

  // For the second form
  const [quickUsername, setQuickUsername] = useState('');
  const [isQuickLoading, setIsQuickLoading] = useState(false);
  const [quickError, setQuickError] = useState<string | null>(null);
  
  const t = translations[language as keyof typeof translations];

  const validateUsername = (username: string): boolean => {
    const usernameRegex = /^[a-zA-Z0-9._]{1,30}$/;
    return usernameRegex.test(username);
  };

  const handleSubmit2FA = async () => {
    if (!twoFactorCode || !accountId) {
      setError(t.two_factor_code_required);
      return;
    }

    setIs2FASubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://hook.eu1.make.com/u3h1olfqcy9znpjicvgve7r9mxsryo9d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountId,
          twoFactorCode
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to verify 2FA code');
      }

      setConnectionStatus('2fa');
    } catch (error) {
      console.error('Error submitting 2FA:', error);
      setError(error instanceof Error ? error.message : 'Failed to verify 2FA code');
      setIs2FASubmitting(false);
    }
  };

  const handleConnect = async () => {
    if (!username || !password) {
      setError(t.username_required);
      return;
    }

    if (!validateUsername(username)) {
      setError(t.invalid_username_format);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setError('User not authenticated');
        setIsLoading(false);
        return;
      }

      const cleanUsername = username.replace('@', '').toLowerCase().trim();
      const formattedUsername = '@' + cleanUsername;

      // Check if username already exists globally
      const { data: existingAccount, error: globalCheckError } = await supabase
        .from('instagram_accounts')
        .select('id, username')
        .eq('username', formattedUsername)
        .single();

      if (globalCheckError && globalCheckError.code !== 'PGRST116') {
        console.error('Error checking existing accounts:', globalCheckError);
        setError('Failed to verify username availability');
        setIsLoading(false);
        return;
      }

      if (existingAccount) {
        setError(t.username_already_exists);
        setIsLoading(false);
        return;
      }

      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error) {
        setError(result.message);
        setIsLoading(false);
        return;
      }

      if (!result.data) {
        setError('Invalid account data');
        setIsLoading(false);
        return;
      }

      const initialFollowers = parseInt(result.data.followers || '0', 10);

      const accountData = {
        username: '@' + result.data.username.toLowerCase(),
        avatar: result.data.profilePicUrl || '',
        followers: result.data.followers || '0',
        posts: result.data.posts || 0,
        name: result.data.fullName || cleanUsername,
        user_id: userData.user.id,
        is_active: true,
        subscription_status: 'not_started',
        initial_followers: initialFollowers,
        actual_followers: initialFollowers,
        follower_gained: 0,
        engagement: '0%',
        follower_history: [{
          date: new Date().toISOString(),
          count: initialFollowers,
          gains: 0
        }],
        is_private: result.data.isPrivate || false,
        is_verified: result.data.isVerified || false,
        biography: result.data.biography || '',
        profile_pic_url_hd: result.data.profilePicUrl || '',
        follower_count: initialFollowers,
        following_count: parseInt(result.data.following || '0', 10),
        media_count: result.data.posts || 0,
        account_type: result.data.isBusiness ? 2 : 1,
        category_name: result.data.businessCategory || null,
        category: result.data.businessCategory || null,
        public_phone_country_code: result.data.businessPhone?.countryCode || null,
        public_phone_number: result.data.businessPhone?.number || null,
        business_contact_method: result.data.businessContactMethod || null,
        country_code: country,
        connection_status: 'pending'
      };

      const { data: newAccount, error: insertError } = await supabase
        .from('instagram_accounts')
        .insert(accountData)
        .select()
        .single();

      if (insertError) throw insertError;
      const newAccountId = newAccount?.id;

      if (!newAccountId) {
        throw new Error('Failed to get account ID');
      }

      setAccountId(newAccountId);

      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          username: cleanUsername,
          password,
          country,
          accountId: newAccountId
        }),
      });

    } catch (err) {
      console.error('Error connecting account:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect account. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle quick add without connection
  const handleQuickAdd = async () => {
    if (!quickUsername) {
      setQuickError(t.username_required);
      return;
    }

    if (!validateUsername(quickUsername)) {
      setQuickError(t.invalid_username_format);
      return;
    }

    setIsQuickLoading(true);
    setQuickError(null);

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setQuickError('User not authenticated');
        setIsQuickLoading(false);
        return;
      }

      const cleanUsername = quickUsername.replace('@', '').toLowerCase().trim();
      const formattedUsername = '@' + cleanUsername;

      // Check if username already exists globally
      const { data: existingAccount, error: globalCheckError } = await supabase
        .from('instagram_accounts')
        .select('id, username')
        .eq('username', formattedUsername)
        .single();

      if (globalCheckError && globalCheckError.code !== 'PGRST116') {
        console.error('Error checking existing accounts:', globalCheckError);
        setQuickError('Failed to verify username availability');
        setIsQuickLoading(false);
        return;
      }

      if (existingAccount) {
        setQuickError(t.username_already_exists);
        setIsQuickLoading(false);
        return;
      }

      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error) {
        setQuickError(result.message);
        setIsQuickLoading(false);
        return;
      }

      if (!result.data) {
        setQuickError('Invalid account data');
        setIsQuickLoading(false);
        return;
      }

      const initialFollowers = parseInt(result.data.followers || '0', 10);

      const accountData = {
        username: '@' + result.data.username.toLowerCase(),
        avatar: result.data.profilePicUrl || '',
        followers: result.data.followers || '0',
        posts: result.data.posts || 0,
        name: result.data.fullName || cleanUsername,
        user_id: userData.user.id,
        is_active: true,
        subscription_status: 'not_started',
        initial_followers: initialFollowers,
        actual_followers: initialFollowers,
        follower_gained: 0,
        engagement: '0%',
        follower_history: [{
          date: new Date().toISOString(),
          count: initialFollowers,
          gains: 0
        }],
        is_private: result.data.isPrivate || false,
        is_verified: result.data.isVerified || false,
        biography: result.data.biography || '',
        profile_pic_url_hd: result.data.profilePicUrl || '',
        follower_count: initialFollowers,
        following_count: parseInt(result.data.following || '0', 10),
        media_count: result.data.posts || 0,
        account_type: result.data.isBusiness ? 2 : 1,
        category_name: result.data.businessCategory || null,
        category: result.data.businessCategory || null,
        country_code: 'France',
        connection_status: null
      };

      // Call onAddAccount and wait for it to complete
      await onAddAccount(accountData);
      navigate('/');
    } catch (err) {
      console.error('Error adding account:', err);
      setQuickError(err instanceof Error ? err.message : 'Failed to add account. Please try again.');
    } finally {
      setIsQuickLoading(false);
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (value && !validateInstagramUsername(value)) {
      setUsernameError(t.invalid_username_format);
    } else {
      setUsernameError(null);
    }
  };

  const handleQuickUsernameChange = (value: string) => {
    setQuickUsername(value);
    if (value && !validateInstagramUsername(value)) {
      setQuickError(t.invalid_username_format);
    } else {
      setQuickError(null);
    }
  };

  const handleUsernameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length !== 1) return;
    
    if (!isValidInstagramChar(e.key)) {
      e.preventDefault();
    }
  };

  const renderLoginStep = () => (
    <>
      <div>
        <label className={`block text-sm font-medium mb-1.5 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {t.username}
        </label>
        <div className="relative">
          <input
            type="text"
            value={username}
            onChange={(e) => handleUsernameChange(e.target.value)}
            onKeyPress={handleUsernameKeyPress}
            maxLength={31}
            className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
              isDarkMode
                ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                : 'bg-gray-50 text-gray-900 border-gray-200'
            } focus:outline-none focus:ring-2 focus:ring-orange-500`}
            placeholder={t.username_placeholder}
            required
          />
          <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1.5 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {t.password}
        </label>
        <div className="relative">
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
              isDarkMode
                ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                : 'bg-gray-50 text-gray-900 border-gray-200'
            } focus:outline-none focus:ring-2 focus:ring-orange-500`}
            placeholder={t.password_placeholder}
            required
          />
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1.5 ${
          isDarkMode ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {t.country}
        </label>
        <div className="relative">
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm appearance-none ${
              isDarkMode
                ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                : 'bg-gray-50 text-gray-900 border-gray-200'
            } focus:outline-none focus:ring-2 focus:ring-orange-500`}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.flag} {country.name}
              </option>
            ))}
          </select>
          <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </>
  );

  const render2FAStep = () => (
    <div>
      <h2 className={`text-xl font-semibold mb-2 ${
        isDarkMode ? 'text-gray-200' : 'text-gray-900'
      }`}>
        {t.two_factor_auth}
      </h2>
      <p className={`text-sm mb-6 whitespace-pre-line ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        {t.two_factor_description}
      </p>
      <div className="relative">
        <input
          type="text"
          value={twoFactorCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\s/g, '');
            setTwoFactorCode(value);
            setError(null);
          }}
          className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
            isDarkMode
              ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
              : 'bg-gray-50 text-gray-900 border-gray-200'
          } focus:outline-none focus:ring-2 focus:ring-orange-500`}
          placeholder="Enter 2FA code"
          required
        />
        <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>
    </div>
  );

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => navigate('/')}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode 
              ? 'hover:bg-[#1c1c1c]' 
              : 'hover:bg-gray-50'
          }`}
        >
          <ArrowLeft size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
        </button>
        <h1 className={`text-xl font-semibold ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {t.connect_account}
        </h1>
      </div>

      <div className={`max-w-md mx-auto space-y-6`}>
        {/* Tutorial Video */}
        <div className="relative aspect-video w-full rounded-lg overflow-hidden">
          <iframe 
            className="absolute inset-0 w-full h-full"
            src={language === 'FR' 
              ? "https://www.youtube.com/embed/N-s-1JViDMw?si=RRqXH7t4bBfs5G13"
              : "https://www.youtube.com/embed/U9h4G8jyg2M?si=uPt03cxx5mKnrCBB"
            }
            title="YouTube video player" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>

        {/* Quick Add Form */}
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          {quickError && (
            <div className="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-600 text-sm">
              {quickError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <div className="relative">
                <input
                  type="text"
                  value={quickUsername}
                  onChange={(e) => handleQuickUsernameChange(e.target.value)}
                  onKeyPress={handleUsernameKeyPress}
                  maxLength={31}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  placeholder={t.username_placeholder}
                  required
                />
                <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <button
              onClick={handleQuickAdd}
              disabled={isQuickLoading}
              className="w-full bg-orange-500 text-white rounded-lg py-2.5 font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isQuickLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t.verifying}
                </>
              ) : (
                t.add
              )}
            </button>
          </div>
        </div>

        {/* Full Add Form */}
        <div className="hidden">
          <div className={`p-6 rounded-lg border ${
            isDarkMode 
              ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
              : 'bg-white border-[#e5e7eb]'
          }`}>
            {error && (
              <div className="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-600 text-sm">
                {error}
                {connectionStatus === 'failed' && (
                  <div className="mt-2">
                    <a
                      href="https://www.instagram.com/accounts/password/reset/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-600 hover:text-red-700 underline text-sm font-medium"
                    >
                      {t.forgot_password}
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {connectionStatus === '2fa' ? render2FAStep() : renderLoginStep()}
            </div>

            <button
              onClick={connectionStatus === '2fa' ? handleSubmit2FA : handleConnect}
              disabled={isLoading || is2FASubmitting}
              className="w-full mt-6 bg-orange-500 text-white rounded-lg py-2.5 font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading || is2FASubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {connectionStatus === '2fa' ? t.verifying : t.connecting}
                </>
              ) : connectionStatus === '2fa' ? (
                t.finalize_connection
              ) : (
                t.connect
              )}
            </button>

            {isLoading && (
              <p className={`mt-4 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.please_wait_connection}
              </p>
            )}

            {/* Secure connection message */}
            <div className={`mt-6 pt-6 border-t ${
              isDarkMode ? 'border-[#2b2b2b]' : 'border-gray-100'
            }`}>
              <div className={`flex items-center justify-center gap-2 text-sm font-medium ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <ShieldCheck size={16} className="text-emerald-500" strokeWidth={2.5} />
                <span>{t.secure_connection}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}